import React, { useMemo, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { useCurrentProject } from '@adsk/alloy-provider';
import Modal from '@adsk/alloy-react-modal';

import EntityPickerModalBody from './EntityPickerModalBody';
import EntityPickerModalFooter from './EntityPickerModalFooter';
import { isPluginProductOrToolNotAvailableInEntitlements } from '../helpers/isPluginProductOrToolNotAvailableInEntitlements';
import { createFormPropsShape, pluginShape } from '../helpers/propTypes';
import useCanCreate from '../hooks/useCanCreate';
import useEntitySelection from '../hooks/useEntitySelection';

const ConnectedEntityPickerModal = ({
  open,
  sourceEntityType,
  plugin,
  closeModal,
  container,
  markAsSelectedIds,
  disabledIds,
  onSave,
  onCreateSuccess,
  isSaving,
  loading,
  error,
  retry,
  allowCreate,
  multiple,
  createFormProps,
  dataViewProps,
  addEntitiesToCache,
  onUpdate,
  saveButtonText,
  title,
  infoMessage,
  renderFooter,
  selectedEntities: selectedEntitiesProp,
  setSelectedEntities: setSelectedEntitiesProp,
}) => {
  const currentProject = useCurrentProject();
  const { selectedEntities, onSelectionChange, setSelection, clearSelection } =
    useEntitySelection({
      multiple,
      selectedEntities: selectedEntitiesProp,
      setSelectedEntities: setSelectedEntitiesProp,
    });

  const isPluginDisabledInEntitlements = useMemo(
    () =>
      isPluginProductOrToolNotAvailableInEntitlements({
        plugin,
        currentProject,
      }),
    [plugin, currentProject],
  );

  const [createPanelOpen, setCreatePanelOpen] = useState(false);

  const manuallySelectedIds = useMemo(
    () => selectedEntities.map((e) => e.id),
    [selectedEntities],
  );
  const selectedIds = useMemo(
    () => [...new Set([...manuallySelectedIds, ...markAsSelectedIds])],
    [manuallySelectedIds, markAsSelectedIds],
  );

  useEffect(() => {
    if (!open) {
      clearSelection();
      setCreatePanelOpen(false);
    }
    // we only want this called when open changes
    // we cannot rely on clearSelection not changing when this component is controlled
    // because we cannot assume that the setSelectedEntities prop will be memoized
    // also we don't care if clearSelection changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const additionalArgs = useMemo(() => {
    if (plugin && plugin.getCanCreateArgs) {
      return plugin.getCanCreateArgs({ createFormProps });
    }

    return null;
  }, [plugin, createFormProps]);

  const userPermissionsData = useCanCreate(
    plugin,
    !allowCreate,
    additionalArgs,
  );

  const modalTitle = title ? title : plugin ? <plugin.PickerModalTitle /> : '';
  const renderFooterComponent =
    renderFooter || ((props) => <EntityPickerModalFooter {...props} />);

  return (
    <Modal
      open={open}
      container={container}
      size={Modal.SIZES.EXTRA_LARGE}
      {...plugin?.modalProps}
    >
      <Modal.Header>
        {modalTitle}
        <Modal.Close onClick={closeModal} />
      </Modal.Header>
      <Modal.Body
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        }}
      >
        <EntityPickerModalBody
          sourceEntityType={sourceEntityType}
          plugin={plugin}
          userPermissionsData={userPermissionsData}
          loading={loading}
          error={error}
          retry={retry}
          setSelection={setSelection}
          onChange={onSelectionChange}
          isSaving={isSaving}
          selectedIds={selectedIds}
          disabledIds={disabledIds}
          multiple={multiple}
          addEntitiesToCache={addEntitiesToCache}
          onUpdate={onUpdate}
          onCreateSuccess={onCreateSuccess}
          createFormProps={createFormProps}
          dataViewProps={dataViewProps}
          createPanelOpen={createPanelOpen}
          setCreatePanelOpen={setCreatePanelOpen}
          infoMessage={infoMessage}
          isPluginDisabledInEntitlements={isPluginDisabledInEntitlements}
        />
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: 'space-between' }}>
        {renderFooterComponent({
          multiple,
          selectedEntities,
          plugin,
          clearSelection,
          isSaving,
          createPanelOpen,
          closeModal,
          onSave,
          saveButtonText,
        })}
      </Modal.Footer>
    </Modal>
  );
};

ConnectedEntityPickerModal.defaultProps = {
  disabledIds: [],
  markAsSelectedIds: [],
};

ConnectedEntityPickerModal.propTypes = {
  /**
   * The plugin to use for selected entity type.
   */
  plugin: pluginShape,
  /**
   * The source entity type, as used by relationship service.
   */
  sourceEntityType: PropTypes.string,
  /**
   * Whether or not the modal is open.
   */
  open: PropTypes.bool,
  /**
   * Handler for closing the modal.
   */
  closeModal: PropTypes.func,
  /**
   * Whether the component is multi selectable.
   */
  multiple: PropTypes.bool,
  /**
   * IDs of entities we need to mark as selected.
   * The component controls the internal state of selected items, this should be done only if you need to mark something as "already selected".
   */
  markAsSelectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of entities we want to mark as disabled.
   */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Save button click handler.
   * Shape: (selectedEntities) => *
   */
  onSave: PropTypes.func.isRequired,
  /**
   * Handler after successful creation of a new entity.
   * Used if `allowCreate` is true.
   */
  onCreateSuccess: PropTypes.func,
  /**
   * Whether we're currently in saving process.
   */
  isSaving: PropTypes.bool,
  /**
   * Whether we currently load data
   */
  loading: PropTypes.bool,
  /**
   * Whether fetching data raised an error
   */
  error: PropTypes.bool,
  /**
   * Retry loading data in case of an error. Required when `error` is true
   */
  retry: PropTypes.func,
  /**
   * Whether or not the modal should support creating new entities.
   */
  allowCreate: PropTypes.bool,
  /**
   * A handle method to add entities to cache.
   */
  addEntitiesToCache: PropTypes.func,
  /**
   * Modal container.
   */
  container: PropTypes.object,
  /**
   * Additional props to pass to create form.
   */
  createFormProps: createFormPropsShape,
  /**
   * Additional props to pass to DataView.
   */
  dataViewProps: PropTypes.object,
  /**
   * Save button text.
   */
  saveButtonText: PropTypes.node,
  /**
   * Modal title text.
   */
  title: PropTypes.node,
  /**
   * Callback to handle an entity update.
   * Handler is of shape: (entity) => *
   */
  onUpdate: PropTypes.func,
  /**
   * Array of selected entities, pass to make this component controlled.
   * Must also pass setSelectedEntities if selectedEntities is passed.
   */
  selectedEntities: PropTypes.array,
  /**
   * Callback to handle entity selection.
   * Handler is of shape: (selectedEntities) => *
   */
  setSelectedEntities: PropTypes.func,
  /**
   * Top bar info message markup
   */
  infoMessage: PropTypes.node,
  /**
   * An optional footer component (will render default footer if not provided).
   * Element will receive the following props:
   * plugin - The plugin to use for selected entity type (passed down from the prop given to EntityPickerModal),
   * multiple - Whether the component is multi selectable (passed down from the prop given to EntityPickerModal),
   * onSave - Save button click handler, Shape: (selectedEntities) => * (passed down from the prop given to EntityPickerModal),
   * isSaving - Whether we're currently in saving process (passed down from the prop given to EntityPickerModal),
   * createPanelOpen - Whether create panel is currently open,
   * selectedEntities - List of currently selected entities,
   * clearSelection - A handle method to clear current selection,
   * closeModal - A handle method to close the modal (passed down from the prop given to EntityPickerModal),
   * saveButtonText - Save button text (passed down from the prop given to EntityPickerModal),
   */
  renderFooter: PropTypes.elementType,
};

export default ConnectedEntityPickerModal;
